import { useEffect, useState } from "react";
import { Loader2, ListChecks, FileText, Clock, ArrowRight, Square, CheckSquare } from "lucide-react";
import { toast } from "sonner";

/**
 * The inner body of a step drill-down: loads an AI execution guide (overview,
 * time estimate, step-by-step how-to, documents to build) and a saved,
 * tickable checklist. Shared by RecoveryStep (roadmap) and ActionPlan (prospect
 * priority boxes).
 *
 * Props:
 *  - active: when true, loads the detail (once)
 *  - loadDetail: async () => { detail, checklist }
 *  - saveItem: async (itemIndex, done) => { checklist }
 *  - testid: base data-testid
 */
export default function StepDetailBody({ active, loadDetail, saveItem, testid = "step-detail" }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (active && !detail && !loading) {
      setLoading(true);
      loadDetail()
        .then((data) => {
          if (cancelled) return;
          setDetail(data.detail);
          setChecklist(data.checklist || []);
        })
        .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't build this step. Try again."); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const tick = async (i) => {
    const snapshot = checklist;
    const optimistic = checklist.map((c, j) => (j === i ? { ...c, done: !c.done } : c));
    setChecklist(optimistic);
    try {
      const { data } = await saveItem(i, !snapshot[i].done);
      if (data?.checklist) setChecklist(data.checklist);
    } catch {
      setChecklist(snapshot);
      toast.error("Couldn't save that.");
    }
  };

  const doneCount = checklist ? checklist.filter((c) => c.done).length : 0;

  if (loading) {
    return (
      <div data-testid={`${testid}-loading`} className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/8 px-4 py-4 my-2">
        <div className="flex items-center gap-2.5 text-sm text-[#1e3d3a]">
          <Loader2 className="h-5 w-5 animate-spin text-[#a3823f]" />
          <span className="font-medium">Building your step-by-step plan…</span>
        </div>
        <p className="mt-1.5 text-xs text-[#1e3d3a]/55">Writing the exact actions, documents to build, and a checklist for this step. This takes a few seconds.</p>
        <div className="mt-3 space-y-2">
          <div className="h-2.5 w-2/3 rounded bg-[#1e3d3a]/8 animate-pulse" />
          <div className="h-2.5 w-full rounded bg-[#1e3d3a]/8 animate-pulse" />
          <div className="h-2.5 w-5/6 rounded bg-[#1e3d3a]/8 animate-pulse" />
        </div>
      </div>
    );
  }
  if (!detail) return null;

  return (
    <div className="space-y-4 pt-3" data-testid={`${testid}-body`}>
      {(detail.overview || detail.time_estimate) && (
        <div className="space-y-2">
          {detail.time_estimate && (
            <span className="inline-flex items-start gap-1 rounded-full bg-[#1e3d3a]/6 px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#1e3d3a]/60">
              <Clock className="h-3 w-3 shrink-0 mt-[1px]" /> {detail.time_estimate}
            </span>
          )}
          {detail.overview && <p className="text-sm text-[#1e3d3a]/70">{detail.overview}</p>}
        </div>
      )}

      {(detail.how_to || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-2 flex items-center gap-1.5">
            <ArrowRight className="h-3 w-3" /> Step by step
          </div>
          <ol className="space-y-1.5">
            {detail.how_to.map((h, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#1e3d3a]/80">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1e3d3a] text-[10px] font-semibold text-[#c9a961]">{i + 1}</span>
                <span className="mt-0.5">{h}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {(detail.documents || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-2 flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Documents to build
          </div>
          <ul className="space-y-1.5">
            {detail.documents.map((d, i) => (
              <li key={i} className="rounded-lg bg-[#eae7de]/50 px-3 py-2">
                <div className="text-sm font-medium text-[#1e3d3a]">{d.name}</div>
                {d.purpose && <div className="text-xs text-[#1e3d3a]/55">{d.purpose}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(checklist || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-2 flex items-center gap-1.5">
            <ListChecks className="h-3 w-3" /> Checklist · {doneCount}/{checklist.length} done
          </div>
          <ul className="space-y-1">
            {checklist.map((c, i) => (
              <li key={i}>
                <button
                  type="button"
                  data-testid={`${testid}-check-${i}`}
                  onClick={() => tick(i)}
                  className="w-full flex items-start gap-2 text-left rounded-lg px-2 py-1.5 hover:bg-[#c9a961]/8 transition-colors"
                >
                  {c.done
                    ? <CheckSquare className="h-4 w-4 text-[#4b7a3a] shrink-0 mt-0.5" />
                    : <Square className="h-4 w-4 text-[#1e3d3a]/35 shrink-0 mt-0.5" />}
                  <span className={`text-sm ${c.done ? "text-[#1e3d3a]/40 line-through" : "text-[#1e3d3a]/80"}`}>{c.text}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
